import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import {
  BookOpen,
  Users,
  ArrowRight,
  Play,
  LogOut,
  Award,
  CheckCircle,
  LayoutDashboard,
  Sprout,
  MessageSquare,
  Brain,
  Crown,
  Mic2,
  BarChart3,
  UserPlus,
  Target,
  Sparkles,
  Library,
  Zap,
  Tags,
  Heart,
  SquareStack,
  Monitor,
  Smartphone,
  Sun,
  Moon,
  LayoutList,
  Smile,
  type LucideIcon
} from 'lucide-react';
import { PersianBackground } from './shared/PersianBackground';
import { AppBrandLogoFrame } from './shared/AppBrandLogo';
import { SiteFooter } from './shared/SiteFooter';
import { HomeHeroPersianDecor, HomeHeroNastaliqPoem } from './HomeHeroPersianDecor';
import { HomeProductDemoModal } from './HomeProductDemoModal';
import { apiService } from '../services/api';
import { useTheme } from '../hooks/useTheme';
import {
  useHomePageContent,
  type HomePageStats,
  type HomeFeatureFooter,
  type HomeFeatureIcon
} from '../hooks/useHomePageContent';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../providers/LocaleProvider';
import { darkenColor } from '../utils/colorUtils';
import { localeMirroredNavArrowClass } from '../utils/localeNavArrow';

type GradientStops = [string, string, string];

export type { HomePageStats } from '../hooks/useHomePageContent';

const LEITNER_BOX_HEIGHTS = [28, 36, 44, 52, 62] as const;

const HOME_REVEAL_EASE = [0.22, 1, 0.36, 1] as const;

/** Title, then description. */
const homeHeaderStagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.16
    }
  }
};

/** Sibling items after their own observer fires. */
const homeItemStagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.04
    }
  }
};

/** Journey tray fades in, then tiles rise one after another. */
const homeJourneyBoxReveal: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: HOME_REVEAL_EASE,
      staggerChildren: 0.18,
      delayChildren: 0.06
    }
  }
};

const homeJourneyTileReveal: Variants = {
  hidden: { opacity: 0, y: 148 },
  visible: (index: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.2,
      ease: HOME_REVEAL_EASE,
      delay: index * 0.18
    }
  })
};

/** Stats cards: box first, then numbers/copy. */
const homeStatBoxReveal: Variants = {
  hidden: { opacity: 0, y: 96 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.82,
      ease: HOME_REVEAL_EASE,
      staggerChildren: 0.12,
      delayChildren: 0.32
    }
  }
};

const HOME_COMPLEMENT_DELAY = 1.18;
const HOME_CHIP_STAGGER = 0.14;
/** After the last of three chips has started and finished sliding in. */
const HOME_FILL_DELAY = HOME_COMPLEMENT_DELAY + HOME_CHIP_STAGGER * 2 + 0.55;

const homeItemReveal: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: HOME_REVEAL_EASE }
  }
};

const homeJourneyIconReveal: Variants = {
  hidden: { opacity: 0, y: 36 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.62, ease: HOME_REVEAL_EASE, delay: 0.58 }
  }
};

const homeJourneyCopyReveal: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.66, ease: HOME_REVEAL_EASE, delay: 0.98 }
  }
};

const homeStatContentReveal: Variants = {
  hidden: { opacity: 0, y: 36 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.62, ease: HOME_REVEAL_EASE, delay: 0.58 }
  }
};

const homeCardReveal: Variants = {
  hidden: { opacity: 0, y: 96 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: HOME_REVEAL_EASE,
      staggerChildren: 0.22,
      delayChildren: 0.46
    }
  }
};

const homeComplementStagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: HOME_CHIP_STAGGER,
      delayChildren: HOME_COMPLEMENT_DELAY
    }
  }
};

const homeFillBar: Variants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.95, ease: HOME_REVEAL_EASE, delay: HOME_FILL_DELAY }
  }
};

const homeFillBox: Variants = {
  hidden: { scaleY: 0 },
  visible: (index: number) => ({
    scaleY: 1,
    transition: {
      duration: 0.95,
      ease: HOME_REVEAL_EASE,
      delay: HOME_FILL_DELAY + index * 0.1
    }
  })
};

const homePassThrough: Variants = {
  hidden: {},
  visible: {
    transition: { when: 'beforeChildren' }
  }
};

const homeSlideReveal = (isRtl: boolean): Variants => ({
  hidden: { opacity: 0, x: isRtl ? 28 : -28 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: HOME_REVEAL_EASE }
  }
});

const homeHighlightRowReveal = (isRtl: boolean): Variants => ({
  hidden: { opacity: 0, x: isRtl ? 28 : -28 },
  visible: (index: number) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: HOME_REVEAL_EASE, delay: 0.58 + index * 0.14 }
  })
});

/** Track & connect live line: after the last numbered row, slide in from the reading start. */
const HOME_PULSE_AFTER_ROWS = 0.28;
const homePulseLineReveal = (isRtl: boolean): Variants => ({
  hidden: { opacity: 0, x: isRtl ? 28 : -28 },
  visible: (rowCount: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: HOME_REVEAL_EASE,
      delay: HOME_COMPLEMENT_DELAY + Math.max(0, rowCount - 1) * HOME_CHIP_STAGGER + HOME_PULSE_AFTER_ROWS
    }
  })
});

const HOME_REVEAL_THRESHOLDS = [0, 0.15, 0.3, 0.45, 0.6, 0.8];

function homeRevealRectState(rect: DOMRect, bottomInset: number): 'unready' | 'past' | 'in' | 'out' {
  if (rect.width < 8 || rect.height < 8) return 'unready';
  const vh = window.innerHeight;
  if (rect.bottom < 8) return 'past';
  const viewTop = vh * 0.04;
  const viewBottom = vh * (1 - bottomInset);
  const visible = Math.min(rect.bottom, viewBottom) - Math.max(rect.top, viewTop);
  if (visible < 8) return 'out';
  const minVisible = Math.min(Math.max(rect.height * 0.28, 48), 80);
  if (visible < minVisible) return 'out';
  return 'in';
}

function homeRevealShouldPlay(rect: DOMRect, bottomInset: number): 'instant' | 'play' | 'wait' {
  const state = homeRevealRectState(rect, bottomInset);
  if (state === 'past') return 'instant';
  if (state !== 'in') return 'wait';
  return 'play';
}

function useHomeReveal(
  enabled = true,
  options?: { bottomInset?: number; threshold?: number | number[] }
) {
  const reduceMotion = useReducedMotion() === true;
  const { locale } = useAppLocale();
  const bottomInset = options?.bottomInset ?? 0.06;
  const threshold = options?.threshold ?? HOME_REVEAL_THRESHOLDS;
  const ref = useRef<HTMLElement | null>(null);
  const [mode, setMode] = useState<'pending' | 'instant' | 'hidden' | 'visible'>(() =>
    reduceMotion || !enabled ? 'instant' : 'pending'
  );

  useLayoutEffect(() => {
    if (!enabled || reduceMotion) {
      setMode('instant');
      return;
    }
    const el = ref.current;
    if (!el) return;

    let stopped = false;
    const playIfInView = () => {
      if (stopped || !el.isConnected) return false;
      const decision = homeRevealShouldPlay(el.getBoundingClientRect(), bottomInset);
      if (decision === 'instant') {
        setMode('instant');
        return true;
      }
      if (decision === 'play') {
        setMode('visible');
        return true;
      }
      return false;
    };

    const rect = el.getBoundingClientRect();
    const ready = document.readyState === 'complete';
    const decision = homeRevealShouldPlay(rect, bottomInset);
    if (decision === 'instant') {
      setMode('instant');
      return;
    }
    if (decision === 'play' && ready) {
      setMode('visible');
      return;
    }

    setMode('hidden');
    const stop = () => {
      if (stopped) return;
      stopped = true;
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('load', onScroll);
    };
    const tryPlay = () => {
      if (playIfInView()) stop();
    };
    const onScroll = () => tryPlay();
    const observer = new IntersectionObserver(
      () => tryPlay(),
      {
        threshold,
        rootMargin: `0px 0px -${Math.round(bottomInset * 100)}% 0px`
      }
    );
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', onScroll);
    }
    observer.observe(el);
    return stop;
  }, [enabled, reduceMotion, locale, bottomInset, threshold]);

  const skipEnter = reduceMotion || !enabled || mode === 'instant';
  return {
    ref,
    initial: skipEnter ? false : 'hidden',
    animate: skipEnter || mode === 'visible' ? 'visible' : 'hidden'
  } as const;
}

function HomeRevealSection({
  className,
  contentClassName = 'mx-auto max-w-7xl',
  children
}: {
  className?: string;
  contentClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={className}>
      <div className={contentClassName}>{children}</div>
    </section>
  );
}

function HomeRevealGroup({
  children,
  className,
  as = 'div',
  variant = 'list',
  observe
}: {
  children: React.ReactNode;
  className?: string;
  as?: 'div' | 'article';
  variant?: 'list' | 'card' | 'journey' | 'stat';
  observe?: boolean;
}) {
  const reduceMotion = useReducedMotion() === true;
  const shouldObserve = observe ?? (variant === 'card' || variant === 'journey' || variant === 'stat');
  const { ref, initial, animate } = useHomeReveal(
    shouldObserve && !reduceMotion,
    variant === 'journey' ? { bottomInset: 0.06, threshold: HOME_REVEAL_THRESHOLDS } : undefined
  );
  const variants =
    variant === 'card'
      ? homeCardReveal
      : variant === 'journey'
        ? homeJourneyBoxReveal
        : variant === 'stat'
          ? homeStatBoxReveal
          : homeItemStagger;
  if (reduceMotion || !shouldObserve) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const MotionTag = as === 'article' ? motion.article : motion.div;
  return (
    <MotionTag
      ref={ref}
      className={className}
      initial={initial}
      animate={animate}
      variants={variants}
    >
      {children}
    </MotionTag>
  );
}

function HomeRevealItem({
  children,
  className,
  style,
  observe = false,
  observeOptions,
  custom,
  variants: variantsProp
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  observe?: boolean;
  observeOptions?: { bottomInset?: number; threshold?: number | number[] };
  custom?: number;
  variants?: Variants;
}) {
  const reduceMotion = useReducedMotion() === true;
  const { ref, initial, animate } = useHomeReveal(observe && !reduceMotion, observeOptions);
  const variants = variantsProp ?? homeItemReveal;
  if (reduceMotion) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  if (observe) {
    return (
      <motion.div
        ref={ref}
        className={className}
        style={style}
        initial={initial}
        animate={animate}
        custom={custom}
        variants={variants}
      >
        {children}
      </motion.div>
    );
  }
  return (
    <motion.div className={className} style={style} custom={custom} variants={variants}>
      {children}
    </motion.div>
  );
}

function HomeRiseList({
  children,
  className,
  as = 'ul',
  dir
}: {
  children: React.ReactNode;
  className?: string;
  as?: 'ul' | 'div';
  dir?: 'rtl' | 'ltr';
}) {
  const reduceMotion = useReducedMotion() === true;
  if (reduceMotion) {
    const Tag = as;
    return (
      <Tag className={className} dir={dir}>
        {children}
      </Tag>
    );
  }
  const MotionTag = as === 'ul' ? motion.ul : motion.div;
  return (
    <MotionTag className={className} dir={dir} variants={homeComplementStagger}>
      {children}
    </MotionTag>
  );
}

function HomeRiseChild({
  children,
  className,
  as = 'div'
}: {
  children: React.ReactNode;
  className?: string;
  as?: 'div' | 'li';
}) {
  const reduceMotion = useReducedMotion() === true;
  if (reduceMotion) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const MotionTag = as === 'li' ? motion.li : motion.div;
  return (
    <MotionTag className={className} variants={homeItemReveal}>
      {children}
    </MotionTag>
  );
}

function HomeSlideList({
  children,
  className,
  isRtl
}: {
  children: React.ReactNode;
  className?: string;
  isRtl: boolean;
}) {
  const reduceMotion = useReducedMotion() === true;
  if (reduceMotion) {
    return (
      <ul className={className} dir={isRtl ? 'rtl' : 'ltr'}>
        {children}
      </ul>
    );
  }
  return (
    <motion.ul className={className} dir={isRtl ? 'rtl' : 'ltr'} variants={homeComplementStagger}>
      {children}
    </motion.ul>
  );
}

function HomeSlideChild({
  children,
  className,
  isRtl,
  as = 'div',
  custom,
  variants: variantsProp
}: {
  children: React.ReactNode;
  className?: string;
  isRtl: boolean;
  as?: 'div' | 'li';
  custom?: number;
  variants?: Variants;
}) {
  const reduceMotion = useReducedMotion() === true;
  if (reduceMotion) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const MotionTag = as === 'li' ? motion.li : motion.div;
  return (
    <MotionTag className={className} custom={custom} variants={variantsProp ?? homeSlideReveal(isRtl)}>
      {children}
    </MotionTag>
  );
}

interface HomePageProps {
  onLoginClick?: () => void;
  onRegisterClick?: () => void;
  onAdminLoginClick?: () => void;
  onDashboardClick?: () => void;
  user?: any;
  onLogout?: () => void;
  onOpenStaticPage?: (key: 'About' | 'Contact' | 'Privacy' | 'Terms') => void;
}

function HomeSectionHeader({
  title,
  subtitle,
  className = ''
}: {
  title: string;
  subtitle?: string;
  className?: string;
}) {
  const reduceMotion = useReducedMotion() === true;
  const { ref, initial, animate } = useHomeReveal(!reduceMotion);
  return (
    <motion.header
      ref={ref}
      className={`mb-8 w-full text-center sm:mb-10 ${className}`}
      initial={initial}
      animate={animate}
      variants={reduceMotion ? undefined : homeHeaderStagger}
    >
      <motion.div variants={reduceMotion ? undefined : homeItemReveal}>
        <h2 className="mx-auto max-w-3xl text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl md:text-4xl">
          {title}
        </h2>
        <div
          className="mx-auto mt-3 hidden h-px w-40 max-w-[50%] rounded-full bg-gradient-to-r from-transparent via-slate-300 to-transparent dark:via-gray-500 sm:block"
          aria-hidden
        />
      </motion.div>
      {subtitle ? (
        <motion.p
          className="mx-auto mt-3 max-w-2xl px-2 text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:mt-4 sm:text-lg"
          variants={reduceMotion ? undefined : homeItemReveal}
        >
          {subtitle}
        </motion.p>
      ) : null}
    </motion.header>
  );
}

function TileDecorCircles({ strong = false }: { strong?: boolean }) {
  if (strong) {
    return (
      <>
        <div
          className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/25 blur-2xl dark:bg-white/[0.07]"
          aria-hidden
        />
        <div className="pointer-events-none absolute -right-4 top-6 h-20 w-20 rounded-full bg-white/15 blur-xl dark:bg-white/[0.04]" />
        <div
          className="pointer-events-none absolute -left-12 bottom-[-1rem] h-36 w-36 rounded-full bg-black/[0.04] blur-2xl dark:bg-black/40"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute left-4 bottom-4 h-16 w-16 rounded-full bg-white/20 blur-lg dark:bg-white/[0.06]"
          aria-hidden
        />
      </>
    );
  }
  return (
    <>
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/35 blur-md dark:bg-white/10" aria-hidden />
      <div className="absolute -left-8 bottom-0 h-28 w-28 rounded-full bg-black/[0.06] blur-md dark:bg-black/25" aria-hidden />
    </>
  );
}

function JourneyStepTile({
  gradient,
  eyebrow,
  title,
  description,
  iconWrapClass,
  iconClass,
  Icon
}: {
  gradient: string;
  eyebrow: string;
  title: string;
  description: string;
  iconWrapClass: string;
  iconClass: string;
  Icon: LucideIcon;
}) {
  return (
    <div
      className="relative h-full overflow-hidden rounded-xl px-3 py-4 sm:px-5 sm:py-6"
      style={{ background: gradient }}
    >
      <TileDecorCircles strong />
      <div className="relative flex flex-col items-center gap-2.5 text-center sm:items-start sm:gap-3 sm:text-left">
        <HomeRevealItem variants={homeJourneyIconReveal}>
          <div
            className={`mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-dashed shadow-inner sm:mx-0 sm:h-[3.25rem] sm:w-[3.25rem] ${iconWrapClass}`}
          >
            <Icon className={`h-5 w-5 sm:h-[1.35rem] sm:w-[1.35rem] ${iconClass}`} strokeWidth={2.25} aria-hidden />
          </div>
        </HomeRevealItem>
        <HomeRevealItem className="min-w-0 flex-1" variants={homeJourneyCopyReveal}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-800/75 dark:text-white/80 sm:text-[11px]">
            {eyebrow}
          </p>
          <h3 className="text-base font-bold tracking-tight text-slate-950 dark:text-white sm:text-xl md:text-2xl">
            {title}
          </h3>
          <p className="mt-0.5 text-[11px] leading-snug text-slate-800/80 dark:text-white/85 sm:text-xs sm:leading-relaxed">
            {description}
          </p>
        </HomeRevealItem>
      </div>
    </div>
  );
}

type FeatureCardProps = {
  gradient: string;
  title: string;
  description: string;
  eyebrow: string;
  bullets: string[];
  Icon: HomeFeatureIcon;
  ribbon?: string;
  footer?: HomeFeatureFooter;
  liveLabel?: string;
  className?: string;
};

function FeaturePanelCard({
  title,
  description,
  eyebrow,
  bullets,
  gradient,
  Icon,
  isRtl = false,
  className = ''
}: FeatureCardProps & { isRtl?: boolean }) {
  return (
    <HomeRevealGroup
      as="article"
      variant="card"
      className={`clay-card relative flex h-full flex-col overflow-hidden p-1 dark:border dark:border-gray-700 sm:p-1.5 ${className}`}
    >
      <div
        className="pointer-events-none absolute inset-1 overflow-hidden rounded-xl"
        style={{ background: gradient }}
        aria-hidden
      >
        <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/35 dark:bg-white/10" />
        <div className="absolute -left-8 bottom-0 h-28 w-28 rounded-full bg-black/[0.06] dark:bg-black/25" />
      </div>
      <HomeRevealItem className="relative z-10 px-4 pt-5 text-center sm:px-5 sm:pt-6 sm:text-start">
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center">
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/85 text-slate-800 shadow-sm dark:bg-black/25 dark:text-white sm:mx-0">
            <Icon className="h-6 w-6" strokeWidth={2} aria-hidden />
          </div>
          <div className="min-w-0 flex-1" dir={isRtl ? 'rtl' : 'ltr'}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-800/75 dark:text-white/80">
              {eyebrow}
            </p>
            <h3 className="text-xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-2xl">{title}</h3>
          </div>
        </div>
      </HomeRevealItem>
      <HomeRevealItem className="relative z-10 px-4 pt-3 text-center sm:px-5 sm:text-start">
        <p
          className="text-xs leading-relaxed text-slate-800/80 dark:text-white/85 sm:text-sm"
          dir={isRtl ? 'rtl' : 'ltr'}
        >
          {description}
        </p>
      </HomeRevealItem>
      {bullets.length > 0 ? (
        <HomeRevealItem
          className="relative z-10 mt-auto px-4 pb-5 pt-5 sm:px-5 sm:pb-6 sm:pt-6"
          variants={homePassThrough}
        >
          <HomeRiseList
            className="space-y-2 border-t border-slate-900/10 pt-4 text-start dark:border-white/15"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            {bullets.map((item) => (
              <HomeRiseChild
                key={item}
                as="li"
                className="flex items-start gap-2 text-xs text-slate-800/85 dark:text-white/85 sm:text-sm"
              >
                <CheckCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-slate-800/70 dark:text-white/75"
                  strokeWidth={2.5}
                  aria-hidden
                />
                <span>{item}</span>
              </HomeRiseChild>
            ))}
          </HomeRiseList>
        </HomeRevealItem>
      ) : null}
    </HomeRevealGroup>
  );
}

/** Open-book layout with spine — for Read & discover */
function DiscoveryBookCard({
  title,
  description,
  eyebrow,
  bullets,
  gradient,
  Icon,
  isRtl = false,
  className = ''
}: FeatureCardProps & { isRtl?: boolean }) {
  return (
    <HomeRevealGroup
      as="article"
      variant="card"
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-sky-200/80 bg-white shadow-md shadow-sky-900/[0.06] ring-1 ring-sky-100/80 transition-shadow hover:shadow-xl hover:shadow-sky-900/10 dark:border-sky-800/40 dark:bg-gray-900 dark:ring-sky-900/30 dark:hover:shadow-sky-950/40 ${className}`}
    >
      <div
        className="absolute inset-y-0 start-0 w-3 sm:w-4"
        style={{ background: gradient }}
        aria-hidden
      >
        <div className="absolute inset-y-3 left-1/2 w-px -translate-x-1/2 bg-white/25" />
        <div className="absolute inset-y-6 left-1/2 w-px -translate-x-1/2 bg-white/15" />
      </div>
      <HomeRevealItem className="relative py-4 ps-7 pe-4 sm:py-5 sm:ps-8 sm:pe-5">
        <div
          className="pointer-events-none absolute right-0 top-0 h-24 w-24 opacity-[0.07] dark:opacity-[0.12]"
          style={{
            backgroundImage: `repeating-linear-gradient(0deg, currentColor 0px, currentColor 1px, transparent 1px, transparent 7px)`
          }}
          aria-hidden
        />
        <div className="flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg shadow-inner ring-2 ring-white/60 dark:ring-white/10"
            style={{ background: gradient }}
          >
            <Icon className="h-5 w-5 text-slate-900 dark:text-white" strokeWidth={2.25} aria-hidden />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-800 dark:bg-sky-950/60 dark:text-sky-200">
              {eyebrow}
            </span>
            <h3 className="mt-1.5 text-lg font-bold tracking-tight text-slate-900 dark:text-white sm:text-xl">
              {title}
            </h3>
          </div>
        </div>
      </HomeRevealItem>
      <HomeRevealItem className="relative border-b border-sky-100/90 px-4 pb-4 ps-7 sm:px-5 sm:ps-8 sm:pb-5 dark:border-sky-900/50">
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{description}</p>
      </HomeRevealItem>
      {bullets.length > 0 ? (
        <HomeRevealItem
          className="relative mt-auto space-y-2 px-4 py-4 ps-7 sm:px-5 sm:ps-8"
          variants={homePassThrough}
        >
          <HomeSlideList className="space-y-2" isRtl={isRtl}>
            {bullets.map((item) => (
              <HomeSlideChild
                key={item}
                as="li"
                isRtl={isRtl}
                className="flex items-center gap-2.5 rounded-lg bg-sky-50/80 px-3 py-2 text-xs font-medium text-slate-700 dark:bg-sky-950/30 dark:text-slate-200 sm:text-sm"
              >
                <BookOpen className="h-3.5 w-3.5 shrink-0 text-sky-600 dark:text-sky-400" strokeWidth={2.5} aria-hidden />
                <span>{item}</span>
              </HomeSlideChild>
            ))}
          </HomeSlideList>
          <div
            className="pointer-events-none absolute bottom-3 right-3 h-8 w-8 rounded-bl-2xl border-b-2 border-r-2 border-sky-200/90 bg-gradient-to-tl from-sky-50 to-transparent dark:border-sky-700/60 dark:from-sky-950/40"
            aria-hidden
          />
        </HomeRevealItem>
      ) : null}
    </HomeRevealGroup>
  );
}

/** Quest / XP ribbon — for Practice & earn */
function XpQuestCard({
  title,
  description,
  eyebrow,
  bullets,
  gradient,
  Icon,
  ribbon,
  footer,
  isRtl = false,
  className = ''
}: FeatureCardProps & { isRtl?: boolean }) {
  const isLeitner = footer === 'boxes';
  const ribbonLabel = ribbon ?? 'XP';
  const reduceMotion = useReducedMotion() === true;
  return (
    <HomeRevealGroup
      as="article"
      variant="card"
      className={`relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white shadow-lg dark:bg-gray-900 ${
        isLeitner
          ? 'border-rose-200/70 shadow-rose-900/[0.08] dark:border-rose-800/45 dark:shadow-rose-950/30'
          : 'border-violet-200/70 shadow-violet-900/[0.08] dark:border-violet-800/45 dark:shadow-violet-950/30'
      } ${className}`}
    >
      <div
        className={`absolute top-6 h-28 w-28 rotate-12 rounded-2xl opacity-20 blur-sm dark:opacity-25 ${
          isRtl ? '-left-8' : '-right-8'
        }`}
        style={{ background: gradient }}
        aria-hidden
      />
      <div
        className={`absolute top-0 z-10 flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-md ${
          isRtl ? 'left-0 rounded-br-xl' : 'right-0 rounded-bl-xl'
        }`}
        style={{ background: gradient }}
      >
        {isLeitner ? (
          <SquareStack className="h-3 w-3" strokeWidth={2.5} aria-hidden />
        ) : (
          <Zap className="h-3 w-3" strokeWidth={2.5} aria-hidden />
        )}
        {ribbonLabel}
      </div>
      <HomeRevealItem className="relative px-5 pt-5 sm:px-6 sm:pt-6">
        <div className="flex items-start gap-4">
          <div
            className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl shadow-lg ring-4 ${
              isLeitner
                ? 'ring-rose-100/80 dark:ring-rose-900/40'
                : 'ring-violet-100/80 dark:ring-violet-900/40'
            }`}
            style={{ background: gradient }}
          >
            <Icon className="h-7 w-7 text-slate-900 dark:text-white" strokeWidth={2} aria-hidden />
            {isLeitner ? null : (
              <Sparkles
                className="absolute -right-1 -top-1 h-4 w-4 text-amber-500 dark:text-amber-400"
                strokeWidth={2.5}
                aria-hidden
              />
            )}
          </div>
          <div className={`min-w-0 flex-1 ${isRtl ? 'pl-10' : 'pr-10'}`}>
            <p
              className={`text-[11px] font-bold uppercase tracking-widest ${
                isLeitner ? 'text-rose-600 dark:text-rose-300' : 'text-violet-600 dark:text-violet-300'
              }`}
            >
              {eyebrow}
            </p>
            <h3 className="mt-0.5 text-xl font-bold tracking-tight text-slate-900 dark:text-white">{title}</h3>
          </div>
        </div>
      </HomeRevealItem>
      <HomeRevealItem className="relative px-5 pt-3 sm:px-6">
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{description}</p>
      </HomeRevealItem>
      <HomeRevealItem
        className="relative mt-auto px-5 pb-5 pt-6 sm:px-6 sm:pb-6 sm:pt-8"
        variants={homePassThrough}
      >
        {bullets.length > 0 ? (
          <HomeSlideList className="flex flex-col items-start gap-2" isRtl={isRtl}>
            {bullets.map((item) => (
              <HomeSlideChild
                key={item}
                as="li"
                isRtl={isRtl}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  isLeitner
                    ? 'border-rose-200/90 bg-rose-50/90 text-rose-900 dark:border-rose-700/50 dark:bg-rose-950/40 dark:text-rose-100'
                    : 'border-violet-200/90 bg-violet-50/90 text-violet-900 dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-100'
                }`}
              >
                {isLeitner ? (
                  <SquareStack
                    className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400"
                    strokeWidth={2.5}
                    aria-hidden
                  />
                ) : (
                  <Award className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" strokeWidth={2.5} aria-hidden />
                )}
                {item}
              </HomeSlideChild>
            ))}
          </HomeSlideList>
        ) : null}
        {isLeitner ? (
          <div className="mt-5 flex items-end gap-1.5" aria-hidden>
            {LEITNER_BOX_HEIGHTS.map((height, index) => (
              <div key={height} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                <div
                  className="w-full overflow-hidden rounded-md bg-rose-100 dark:bg-rose-950/60"
                  style={{ height }}
                >
                  <motion.div
                    className="h-full w-full origin-bottom rounded-md bg-gradient-to-t from-rose-500 to-rose-300 shadow-sm dark:from-rose-700 dark:to-rose-400"
                    custom={index}
                    variants={reduceMotion ? undefined : homeFillBox}
                  />
                </div>
                <span className="text-[10px] font-bold tabular-nums text-rose-700 dark:text-rose-300">{index + 1}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5 flex items-center gap-2" aria-hidden>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-violet-100 dark:bg-violet-950/60">
              <motion.div
                className={`h-full w-[72%] rounded-full bg-gradient-to-r from-violet-500 to-amber-500 ${
                  isRtl ? 'origin-right' : 'origin-left'
                }`}
                variants={reduceMotion ? undefined : homeFillBar}
              />
            </div>
            <span className="text-[10px] font-bold tabular-nums text-violet-600 dark:text-violet-300">+XP</span>
          </div>
        )}
      </HomeRevealItem>
    </HomeRevealGroup>
  );
}

/** Dashboard + social pulse — for Track & connect */
function InsightConnectCard({
  title,
  description,
  eyebrow,
  bullets,
  gradient,
  Icon,
  footer,
  liveLabel,
  isRtl = false,
  className = ''
}: FeatureCardProps & { isRtl?: boolean }) {
  const reduceMotion = useReducedMotion() === true;
  const isTelegram = footer === 'telegram';
  const barHeights = [40, 65, 48, 80, 55];
  const pulseLabel = liveLabel ?? 'Live community activity';
  return (
    <HomeRevealGroup
      as="article"
      variant="card"
      className={`flex h-full flex-col overflow-hidden rounded-2xl border bg-white shadow-md ring-1 dark:bg-gray-900 ${
        isTelegram
          ? 'border-sky-200/70 ring-sky-100/60 dark:border-sky-800/40 dark:ring-sky-900/30'
          : 'border-emerald-200/70 ring-emerald-100/60 dark:border-emerald-800/40 dark:ring-emerald-900/30'
      } ${className}`}
    >
      <HomeRevealItem
        className="relative px-5 pb-4 pt-5 text-white sm:px-6"
        style={{ background: gradient }}
      >
        <TileDecorCircles />
        <div className="relative flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30">
              <Icon className="h-5 w-5" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/80">{eyebrow}</p>
              <h3 className="text-lg font-bold tracking-tight sm:text-xl">{title}</h3>
            </div>
          </div>
          {isTelegram ? (
            <div className="flex shrink-0 flex-col items-end gap-1 opacity-90" aria-hidden>
              <div className="h-2 w-10 rounded-full bg-white/75" />
              <div className="h-2 w-7 rounded-full bg-white/50" />
              <div className="h-2 w-12 rounded-full bg-white/85" />
            </div>
          ) : (
            <div className="flex shrink-0 items-end gap-0.5 opacity-90" aria-hidden>
              {barHeights.map((h, i) => (
                <div
                  key={i}
                  className="w-1.5 rounded-t-sm bg-white/70"
                  style={{ height: `${h * 0.22}px` }}
                />
              ))}
            </div>
          )}
        </div>
      </HomeRevealItem>
      <HomeRevealItem className="relative px-5 pb-1 pt-4 sm:px-6">
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{description}</p>
      </HomeRevealItem>
      <HomeRevealItem className="relative mt-auto px-5 pb-4 pt-3 sm:px-6" variants={homePassThrough}>
        <HomeRiseList as="div" className="grid gap-2" dir={isRtl ? 'rtl' : 'ltr'}>
          {bullets.map((item, i) => (
            <HomeRiseChild
              key={item}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                isTelegram
                  ? 'border-sky-100/90 bg-sky-50/50 dark:border-sky-900/40 dark:bg-sky-950/25'
                  : 'border-emerald-100/90 bg-emerald-50/50 dark:border-emerald-900/40 dark:bg-emerald-950/25'
              }`}
            >
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                  isTelegram
                    ? i % 2 === 0
                      ? 'bg-sky-200/80 text-sky-900 dark:bg-sky-800/50 dark:text-sky-100'
                      : 'bg-cyan-200/80 text-cyan-900 dark:bg-cyan-800/50 dark:text-cyan-100'
                    : i % 2 === 0
                      ? 'bg-emerald-200/80 text-emerald-900 dark:bg-emerald-800/50 dark:text-emerald-100'
                      : 'bg-teal-200/80 text-teal-900 dark:bg-teal-800/50 dark:text-teal-100'
                }`}
              >
                {i + 1}
              </div>
              <span className="text-xs font-medium text-slate-700 dark:text-slate-200 sm:text-sm">{item}</span>
            </HomeRiseChild>
          ))}
        </HomeRiseList>
        <HomeSlideChild
          isRtl={isRtl}
          custom={bullets.length}
          variants={reduceMotion ? undefined : homePulseLineReveal(isRtl)}
          className={`mt-2 flex items-center gap-1.5 text-[11px] font-medium ${
            isTelegram
              ? 'text-sky-700/80 dark:text-sky-400/90'
              : 'text-emerald-700/80 dark:text-emerald-400/90'
          }`}
        >
          <span className="relative flex h-2 w-2">
            <span
              className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
                isTelegram ? 'bg-sky-400' : 'bg-emerald-400'
              }`}
            />
            <span
              className={`relative inline-flex h-2 w-2 rounded-full ${
                isTelegram ? 'bg-sky-500' : 'bg-emerald-500'
              }`}
            />
          </span>
          {pulseLabel}
        </HomeSlideChild>
      </HomeRevealItem>
    </HomeRevealGroup>
  );
}

function LookFeelPreviewFrame({
  className,
  children
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border border-slate-200/90 bg-white/80 shadow-sm dark:border-slate-600/80 dark:bg-slate-900/50 ${className ?? ''}`}
    >
      {children}
    </div>
  );
}

function LookFeelSection({ isRtl }: { isRtl: boolean }) {
  const { t } = useTranslation('home');
  const text = (key: string, en: string, fa: string) =>
    t(key, { defaultValue: isRtl ? fa : en });

  const cards = [
    {
      key: 'responsive',
      eyebrow: text('lookFeel.responsive.eyebrow', 'Responsive', 'واکنش‌گرا'),
      title: text('lookFeel.responsive.title', 'Phone and desktop', 'تلفن و رایانه'),
      description: text(
        'lookFeel.responsive.description',
        'Lessons, the library, and your home stay comfortable on a small screen and on a wide one.',
        'درس‌ها، کتابخانه و خانه روی صفحهٔ کوچک و صفحهٔ پهن راحت می‌مانند.'
      ),
      preview: (
        <div className="flex items-end justify-center gap-3" aria-hidden>
          <LookFeelPreviewFrame className="w-[4.25rem] p-1.5">
            <div className="mb-1.5 flex justify-center">
              <Smartphone className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
            </div>
            <div className="space-y-1">
              <div className="h-7 rounded-md bg-amber-100 dark:bg-amber-900/50" />
              <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700" />
              <div className="h-1.5 w-2/3 rounded-full bg-slate-200 dark:bg-slate-700" />
            </div>
          </LookFeelPreviewFrame>
          <LookFeelPreviewFrame className="w-[7.5rem] p-2">
            <div className="mb-1.5 flex items-center gap-1">
              <Monitor className="h-3.5 w-3.5 text-sky-700 dark:text-sky-300" />
              <div className="h-1.5 flex-1 rounded-full bg-slate-200 dark:bg-slate-700" />
            </div>
            <div className="grid grid-cols-3 gap-1">
              <div className="h-8 rounded-md bg-amber-100 dark:bg-amber-900/40" />
              <div className="h-8 rounded-md bg-sky-100 dark:bg-sky-900/40" />
              <div className="h-8 rounded-md bg-violet-100 dark:bg-violet-900/40" />
            </div>
          </LookFeelPreviewFrame>
        </div>
      )
    },
    {
      key: 'theme',
      eyebrow: text('lookFeel.theme.eyebrow', 'Your taste', 'سلیقهٔ شما'),
      title: text('lookFeel.theme.title', 'Choose a template', 'یک قالب انتخاب کنید'),
      description: text(
        'lookFeel.theme.description',
        'Advanced keeps every detail open. Simple uses large tiles and one step at a time.',
        'پیشرفته همه‌چیز را باز نگه می‌دارد. ساده از کاشی‌های بزرگ و هر بار یک قدم استفاده می‌کند.'
      ),
      preview: (
        <div className="space-y-2" aria-hidden>
          <div className="flex items-center gap-2 rounded-xl bg-[#3f2a56] px-3 py-2 text-[#fff8ee]">
            <LayoutList className="h-4 w-4 shrink-0" />
            <span className="text-xs font-semibold">{text('lookFeel.theme.advanced', 'Advanced', 'پیشرفته')}</span>
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-[#e4d5c3] bg-[#fffdf8] px-3 py-2 text-[#6b4b2a] dark:border-[#3d342c] dark:bg-[#1c1714] dark:text-[#e6d3bc]">
            <Smile className="h-4 w-4 shrink-0" />
            <span className="text-xs font-semibold">{text('lookFeel.theme.simple', 'Simple', 'ساده')}</span>
          </div>
        </div>
      )
    },
    {
      key: 'modes',
      eyebrow: text('lookFeel.modes.eyebrow', 'Two modes', 'دو حالت'),
      title: text('lookFeel.modes.title', 'Light and dark', 'روشن و تیره'),
      description: text(
        'lookFeel.modes.description',
        'Switch the whole app. Light for daytime, dark when you want a quieter screen.',
        'کل برنامه را عوض کنید. روشن برای روز، تیره وقتی صفحهٔ آرام‌تری می‌خواهید.'
      ),
      preview: (
        <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-600" aria-hidden>
          <div className="bg-[#fffdf8] px-3 py-3.5 text-[#2b221b]">
            <Sun className="h-5 w-5 text-amber-500" />
            <p className="mt-2 text-xs font-semibold">{text('lookFeel.modes.light', 'Light', 'روشن')}</p>
          </div>
          <div className="bg-[#1c1714] px-3 py-3.5 text-[#f6efe6]">
            <Moon className="h-5 w-5 text-amber-200" />
            <p className="mt-2 text-xs font-semibold">{text('lookFeel.modes.dark', 'Dark', 'تیره')}</p>
          </div>
        </div>
      )
    }
  ];

  return (
    <HomeRevealSection className="bg-gradient-to-b from-stone-50 via-amber-50/50 to-white px-4 py-14 dark:from-gray-950 dark:via-amber-950/20 dark:to-gray-900 sm:px-6 sm:py-16 lg:px-8">
      <HomeSectionHeader
        title={text('lookFeel.title', 'Look and feel', 'ظاهر و حس')}
        subtitle={text(
          'lookFeel.subtitle',
          'The app fits your screen, your taste, and the light in the room.',
          'برنامه با صفحه، سلیقه و نور اتاق شما هماهنگ است.'
        )}
      />
      <HomeRevealGroup className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
        {cards.map((card) => (
          <HomeRevealGroup
            key={card.key}
            as="article"
            variant="card"
            className="clay-card relative flex h-full flex-col overflow-hidden p-1 dark:border dark:border-gray-700 md:last:col-span-2 md:last:mx-auto md:last:max-w-lg lg:last:col-span-1 lg:last:mx-0 lg:last:max-w-none"
          >
            <div className="relative flex h-full flex-col rounded-xl bg-gradient-to-br from-white via-amber-50/40 to-stone-50 px-4 py-5 dark:from-slate-900 dark:via-amber-950/20 dark:to-slate-950 sm:px-5 sm:py-6">
              <HomeRevealItem>{card.preview}</HomeRevealItem>
              <HomeRevealItem className="mt-4 text-center sm:text-start">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/80 dark:text-amber-200/80">
                  {card.eyebrow}
                </p>
                <h3 className="mt-1 text-xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-2xl">
                  {card.title}
                </h3>
                <p
                  className="mt-2 text-xs leading-relaxed text-slate-700 dark:text-slate-300 sm:text-sm"
                  dir={isRtl ? 'rtl' : 'ltr'}
                >
                  {card.description}
                </p>
              </HomeRevealItem>
            </div>
          </HomeRevealGroup>
        ))}
      </HomeRevealGroup>
    </HomeRevealSection>
  );
}

function renderFeatureCard(
  variant: 'panel' | 'discovery' | 'quest' | 'insight',
  props: FeatureCardProps,
  isRtl = false
) {
  switch (variant) {
    case 'discovery':
      return <DiscoveryBookCard {...props} isRtl={isRtl} />;
    case 'quest':
      return <XpQuestCard {...props} isRtl={isRtl} />;
    case 'insight':
      return <InsightConnectCard {...props} isRtl={isRtl} />;
    default:
      return <FeaturePanelCard {...props} isRtl={isRtl} />;
  }
}

function CommunityStatCard({
  value,
  label,
  detail,
  accentBarClass,
  watermarkIconClass,
  Icon,
  isRtl = false
}: {
  value: string;
  label: string;
  detail: string;
  accentBarClass: string;
  watermarkIconClass: string;
  Icon: LucideIcon;
  isRtl?: boolean;
}) {
  return (
    <HomeRevealGroup
      as="article"
      variant="stat"
      className="clay-card group relative min-h-[7.5rem] overflow-hidden bg-white/95 p-4 ps-5 transition-shadow hover:shadow-lg dark:border dark:border-gray-700 dark:bg-gray-900/90 sm:min-h-[8rem] sm:p-5 sm:ps-6"
    >
      <div className={`absolute bottom-0 start-0 top-0 w-1.5 ${accentBarClass}`} aria-hidden />
      <Icon
        className={`pointer-events-none absolute -bottom-3 h-[4.5rem] w-[4.5rem] opacity-[0.08] transition-opacity group-hover:opacity-[0.14] dark:opacity-[0.12] dark:group-hover:opacity-[0.18] sm:h-20 sm:w-20 ${
          isRtl ? '-left-3' : '-right-3'
        } ${watermarkIconClass}`}
        strokeWidth={1.25}
        aria-hidden
      />
      <HomeRevealItem className="relative flex h-full flex-col justify-end" variants={homeStatContentReveal}>
        <p className="text-[2rem] font-bold leading-none tabular-nums tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          {value}
        </p>
        <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">{label}</p>
        <p className="mt-0.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400 sm:text-xs">{detail}</p>
      </HomeRevealItem>
    </HomeRevealGroup>
  );
}

function CommunityHighlightsPanel({
  items,
  isRtl = false
}: {
  items: { key: string; label: string; value: string; Icon: LucideIcon }[];
  isRtl?: boolean;
}) {
  const reduceMotion = useReducedMotion() === true;
  return (
    <div
      className="clay-card divide-y divide-slate-200/90 overflow-hidden bg-slate-50/90 dark:divide-gray-700 dark:border dark:border-gray-700 dark:bg-gray-900/50"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      {items.map((item, index) => {
        const RowIcon = item.Icon;
        return (
          <HomeSlideChild
            key={item.key}
            isRtl={isRtl}
            custom={index}
            variants={reduceMotion ? undefined : homeHighlightRowReveal(isRtl)}
            className="flex items-center justify-between gap-4 px-4 py-3.5 sm:px-6 sm:py-4"
          >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100/90 text-amber-800 ring-1 ring-amber-200/80 dark:bg-amber-950/50 dark:text-amber-200 dark:ring-amber-800/50">
                  <RowIcon className="h-4 w-4" strokeWidth={2.25} aria-hidden />
                </div>
                <span className="text-xs font-medium uppercase tracking-wide text-slate-600 dark:text-slate-400 sm:text-sm">
                  {item.label}
                </span>
              </div>
              <span className="shrink-0 text-lg font-bold tabular-nums text-slate-900 dark:text-white sm:text-xl">
                {item.value}
              </span>
            </HomeSlideChild>
          );
        })}
    </div>
  );
}

export const HomePage: React.FC<HomePageProps> = ({
  onLoginClick,
  onRegisterClick,
  onAdminLoginClick,
  onDashboardClick,
  user,
  onOpenStaticPage
}) => {
  const { t } = useTranslation('home');
  const { isDark } = useTheme();
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const {
    learningJourneySteps,
    platformFeatures,
    featureShowcaseSections,
    primaryStats,
    secondaryStats,
  } = useHomePageContent(isDark);
  const [stats, setStats] = useState<HomePageStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [demoOpen, setDemoOpen] = useState(false);

  const makeGradient = useCallback(
    (light: GradientStops, dark: GradientStops, mirror = false) => {
      const stops = isDark ? dark : light;
      const adjusted = stops.map((stop) => (isDark ? darkenColor(stop, 0.35) : stop));
      const angle = mirror ? 315 : 135;
      return `linear-gradient(${angle}deg, ${adjusted.join(', ')})`;
    },
    [isDark]
  );

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await apiService.get<HomePageStats>('/api/Health/home-stats', { cache: false });
        const statsData = (data as { data?: HomePageStats })?.data || data;
        if (statsData) setStats(statsData);
      } catch (error) {
        console.error('Error fetching home page stats:', error);
        try {
          const { errorLoggingService } = await import('../services/errorLoggingService');
          errorLoggingService.logApiError(
            error instanceof Error ? error : new Error(String(error)),
            '/api/Health/home-stats',
            undefined,
            { source: 'HomePage' }
          );
        } catch {
          /* ignore */
        }
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <PersianBackground variant="elegant" showFerdowsi={true} showPattern={true} opacity={0.15} className="w-full">
      <section className="relative isolate w-full pt-8 pb-10 bg-gradient-to-b from-slate-50 to-white dark:from-gray-950 dark:to-gray-900">
        <HomeHeroPersianDecor />
        <div className="relative z-[1] mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-col items-center md:mb-10">
            <div className="relative mb-3">
              <div
                className="absolute inset-0 scale-110 rounded-full bg-amber-500/35 blur-2xl dark:bg-amber-400/15"
                aria-hidden
              />
              <AppBrandLogoFrame alt="" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-900 dark:text-amber-400/95 md:text-sm">
              {t('hero.brand')}
            </p>
          </div>
          <h1 className="mb-0 overflow-visible text-4xl font-bold text-gray-900 dark:text-gray-100 sm:text-5xl md:text-7xl">
            {t('hero.learnPersianLine1')}{' '}
            <span className="bg-gradient-to-r from-amber-700 via-amber-600 to-amber-800 bg-clip-text text-transparent dark:from-amber-400 dark:via-amber-300 dark:to-amber-500">
              {t('hero.learnPersianHighlight')}
            </span>
            <br />
            <span className="relative z-10 mt-2 inline-block overflow-visible text-3xl font-semibold sm:text-4xl md:text-6xl">
              <HomeHeroNastaliqPoem />
              {t('hero.learnPersianPoem')}
            </span>
          </h1>
          <div className="mt-8 flex flex-col gap-4 sm:mt-10 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => {
                if (user) onDashboardClick?.();
                else onRegisterClick?.();
              }}
              className="flex items-center justify-center gap-2.5 rounded-2xl bg-gradient-to-r from-amber-600 to-amber-700 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all hover:from-amber-700 hover:to-amber-800 dark:from-amber-500 dark:to-amber-600"
            >
              <Play className="h-5 w-5 shrink-0" />
              {user ? t('hero.ctaContinue') : t('hero.ctaStartFree')}
            </button>
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="flex items-center justify-center gap-2.5 rounded-2xl border border-gray-200 bg-gray-50 px-8 py-4 text-lg font-semibold text-gray-700 shadow-lg transition-all hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <BookOpen className="h-5 w-5 shrink-0" />
              {t('hero.viewDemo')}
            </button>
          </div>
          {!user && (
            <div className="mt-8 text-center">
              <p className="text-gray-600 dark:text-gray-300 mb-4">{t('hero.alreadyHaveAccount')}</p>
              <button
                type="button"
                onClick={onLoginClick}
                className="inline-flex items-center gap-2.5 rounded-xl border-2 border-amber-600 bg-gray-50 px-6 py-3 font-semibold text-amber-800 shadow-md transition-all hover:bg-amber-50 dark:border-amber-500 dark:bg-gray-800/80 dark:text-amber-300 dark:hover:bg-amber-950/40"
              >
                <LogOut className="h-4 w-4 shrink-0 rotate-180" />
                {t('hero.signIn')}
              </button>
            </div>
          )}
        </div>
      </section>

      <HomeRevealSection className="px-4 pb-16 pt-6 sm:px-6 sm:pt-8 lg:px-8 bg-gradient-to-b from-emerald-50/70 to-teal-50/40 dark:from-emerald-950/15 dark:to-gray-900">
        <HomeSectionHeader
          title={t('journey.title')}
          subtitle={t('journey.subtitle')}
        />
        <HomeRevealGroup
          observe
          variant="journey"
          className="clay-card grid grid-cols-2 gap-1 p-1 dark:border dark:border-gray-700 sm:gap-1.5 sm:p-1.5 lg:grid-cols-4"
        >
          {learningJourneySteps.map((item, index) => (
            <HomeRevealItem
              key={item.level}
              observe
              observeOptions={{ bottomInset: 0.06, threshold: HOME_REVEAL_THRESHOLDS }}
              className="relative min-w-0"
              variants={homePassThrough}
            >
              <motion.div className="relative h-full" custom={index} variants={homeJourneyTileReveal}>
                <JourneyStepTile
                  gradient={makeGradient(item.gradientLight, item.gradientDark, isRtl)}
                  eyebrow={item.eyebrow}
                  title={item.level}
                  description={item.description}
                  iconWrapClass={item.iconWrapClass}
                  iconClass={item.iconClass}
                  Icon={item.Icon}
                />
                {index < learningJourneySteps.length - 1 ? (
                  <div
                    className={`pointer-events-none absolute top-1/2 z-10 hidden -translate-y-1/2 lg:block ${
                      isRtl ? '-left-2' : '-right-2'
                    }`}
                  >
                    <ArrowRight
                      className={localeMirroredNavArrowClass(
                        isRtl,
                        'h-5 w-5 text-slate-400/80 dark:text-slate-500'
                      )}
                      aria-hidden
                    />
                  </div>
                ) : null}
              </motion.div>
            </HomeRevealItem>
          ))}
        </HomeRevealGroup>
      </HomeRevealSection>

      {featureShowcaseSections.map((section) => (
        <HomeRevealSection
          key={section.id}
          className={`py-14 sm:py-16 px-4 sm:px-6 lg:px-8 ${section.sectionClass}`}
        >
          <HomeSectionHeader title={section.title} subtitle={section.subtitle} />
          <HomeRevealGroup
            className={`grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 ${
              section.featureIndexes.length >= 3 ? 'lg:grid-cols-3' : ''
            }`}
          >
            {section.featureIndexes.map((index) => {
              const feature = platformFeatures[index];
              if (!feature) return null;
              const cardClass = `h-full${
                section.featureIndexes.length >= 3
                  ? ' md:last:col-span-2 md:last:mx-auto md:last:max-w-lg lg:last:col-span-1 lg:last:mx-0 lg:last:max-w-none'
                  : ''
              }`;
              return React.cloneElement(
                renderFeatureCard(
                  section.cardVariant,
                  {
                    title: feature.title,
                    description: feature.description,
                    eyebrow: feature.eyebrow,
                    bullets: feature.bullets,
                    Icon: feature.Icon,
                    gradient: makeGradient(feature.gradientLight, feature.gradientDark),
                    footer: feature.footer,
                    ribbon: feature.ribbon,
                    liveLabel: feature.liveLabel,
                    className: cardClass
                  },
                  isRtl
                ),
                { key: feature.title }
              );
            })}
          </HomeRevealGroup>
        </HomeRevealSection>
      ))}

      <LookFeelSection isRtl={isRtl} />

      <HomeRevealSection className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8 bg-gradient-to-b from-rose-50/60 via-amber-50/50 to-orange-50/40 dark:from-rose-950/15 dark:via-gray-900 dark:to-gray-900">
        <HomeSectionHeader
          title={t('stats.title')}
          subtitle={t('stats.subtitle')}
        />
        {statsLoading ? (
          <HomeRevealItem observe>
            <div className="flex justify-center items-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-amber-600 dark:border-amber-400" />
            </div>
          </HomeRevealItem>
        ) : stats ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:gap-5">
            {primaryStats.map((item) => (
              <CommunityStatCard
                key={item.key}
                value={item.format(stats)}
                label={item.label}
                detail={item.detail}
                accentBarClass={item.accentBarClass}
                watermarkIconClass={item.watermarkIconClass}
                Icon={item.Icon}
                isRtl={isRtl}
              />
            ))}
            <HomeRevealGroup observe variant="stat" className="col-span-2 lg:col-span-4">
              <HomeRevealItem variants={homeStatContentReveal}>
                <p className="mb-4 text-center text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {t('stats.highlights')}
                </p>
              </HomeRevealItem>
              <HomeRevealItem variants={homePassThrough}>
                <CommunityHighlightsPanel
                  isRtl={isRtl}
                  items={secondaryStats.map((item) => ({
                    key: item.key,
                    label: item.label,
                    value: item.format(stats),
                    Icon: item.Icon
                  }))}
                />
              </HomeRevealItem>
            </HomeRevealGroup>
          </div>
        ) : (
          <HomeRevealItem observe>
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">No statistics available</div>
          </HomeRevealItem>
        )}
      </HomeRevealSection>

      <HomeRevealSection
        className="px-4 py-16 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-100/90 via-amber-50/40 to-slate-50/80 dark:from-gray-950 dark:via-amber-950/10 dark:to-gray-900"
        contentClassName="mx-auto max-w-4xl text-center"
      >
        <HomeSectionHeader title={t('getStarted.title')} subtitle={t('getStarted.subtitle')} />
        <HomeRevealGroup>
          <HomeRevealItem observe>
            <p className="mx-auto -mt-4 mb-8 max-w-2xl text-lg text-gray-600 dark:text-gray-300 sm:-mt-6">
              {t('getStarted.blurb')}
            </p>
          </HomeRevealItem>
          <HomeRevealItem observe>
            <button
              type="button"
              onClick={() => {
                if (user) onDashboardClick?.();
                else onRegisterClick?.();
              }}
              className="mx-auto flex w-full max-w-md items-center justify-center gap-2.5 rounded-2xl bg-gradient-to-r from-amber-600 to-amber-700 px-12 py-4 text-xl font-semibold text-white shadow-lg transition-all hover:from-amber-700 hover:to-amber-800 dark:from-amber-500 dark:to-amber-600 sm:w-auto"
            >
              {user && <LayoutDashboard className="h-5 w-5 shrink-0" />}
              {user ? t('getStarted.ctaDashboard') : t('getStarted.ctaStartToday')}
            </button>
          </HomeRevealItem>
        </HomeRevealGroup>
      </HomeRevealSection>

      <SiteFooter
        showVersionJourney={!!user}
        {...(onOpenStaticPage ? { onOpenStaticPage } : {})}
        {...(onAdminLoginClick ? { onAdminLoginClick } : {})}
      />

      <HomeProductDemoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        isSignedIn={!!user}
        onStartLearning={() => {
          if (user) onDashboardClick?.();
          else onRegisterClick?.();
        }}
      />
    </PersianBackground>
  );
};

export default HomePage;
